import {
  Component,
  inject,
  signal,
  viewChild,
  ElementRef,
  Signal,
  WritableSignal
} from '@angular/core';

import { TranslatePipe } from '../../../pipes/translate.pipe';
import { IconComponent } from '../../icon/icon.component';
import { FocusOnDisabledDirective } from '../../focus/focus-on-disabled.directive';
import { DatePickerButtonComponent } from '../date-picker-button/date-picker-button.component';
import { DatePickerInnerComponent } from '../date-picker-inner/date-picker-inner.component';

import type { DateObject } from '../date-picker-inner/date-picker-inner.domain';
import type { OnInit } from '@angular/core';

@Component({
  selector: 'td-wb-year-picker',
  templateUrl: './year-picker.component.html',
  styleUrls: ['./year-picker.component.scss'],
  imports: [DatePickerButtonComponent, FocusOnDisabledDirective, IconComponent, TranslatePipe]
})
export class YearPickerComponent implements OnInit {
  readonly yearButtonRef: Signal<ElementRef | undefined> = viewChild<ElementRef>('yearButton');

  // ── DI ──────────────────────────────────────────────────────────────────
  protected readonly datePicker = inject(DatePickerInnerComponent);

  /**
   * Title of year view
   */
  readonly title: WritableSignal<string | undefined> = signal<string | undefined>(undefined);

  /**
   * Array of years to display
   */
  readonly rows: WritableSignal<Array<Array<DateObject>>> = signal<Array<Array<DateObject>>>([]);

  /**
   * Boolean for determining whether previous button should be disabled
   */
  readonly navPrevDisabled: WritableSignal<boolean> = signal<boolean>(false);

  /**
   * Boolean for determining whether next button should be disabled
   */
  readonly navNextDisabled: WritableSignal<boolean> = signal<boolean>(false);

  /**
   * This function registers handlers and initializes step
   */
  ngOnInit(): void {
    this.datePicker.step.set('year', { years: this.datePicker.yearRange() });
    this.datePicker.setRefreshViewHandler((): void => this.refreshView(), 'year');
    this.datePicker.setCompareHandler(
      (date1: Date, date2: Date): number => this.compareDates(date1, date2), 'year');
    this.datePicker.refreshView();
  }

  /**
   * This function is the refresh view handler for date picker inner component
   */
  refreshView(): void {
    const datePicker = this.datePicker;
    const years = new Array<DateObject>(datePicker.yearRange());
    const start = this.getStartingYear(datePicker.activeDate.getFullYear());
    let date: Date;

    for (let i = 0; i < datePicker.yearRange(); i++) {
      date = new Date(start + i, 0, 1);
      years[i] = datePicker.createDateObject(date, datePicker.formatYear());
      years[i].uid = datePicker.uniqueId + '-' + i;
    }

    this.navPrevDisabled.set((datePicker.minDate() !== undefined) && (years[0].date <= datePicker.minDate()));
    this.navNextDisabled.set((datePicker.maxDate() !== undefined) &&
      (new Date(years[datePicker.yearRange() - 1].date.getFullYear() + 1, 0, 1) > datePicker.maxDate()));

    this.title.set([years[0].label, years[datePicker.yearRange() - 1].label].join(' - '));
    this.rows.set(datePicker.split(years, 5) as Array<Array<DateObject>>);
    datePicker.updateActiveDateId(years);
  }

  /**
   * This function is the compare handler for date picker inner component
   *
   * @param date1 Date to compare
   * @param date2 Date to compare
   * @returns Returns comparison of two dates
   */
  compareDates(date1: Date, date2: Date): number {
    return date1.getFullYear() - date2.getFullYear();
  }

  /**
   * This function calculates the starting year based on year range
   *
   * @param year year number
   * @returns Returns starting year
   */
  protected getStartingYear(year: number): number {
    return (
      (year - 1) / this.datePicker.yearRange() * this.datePicker.yearRange() + 1
    );
  }
}
